import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import {
  useOrderOrgProperties,
  useUpdateOrderOrgProperties,
} from "@/management/hooks/useOrderOrgProperties"
import { OrgProperties, RouteParams } from "@/shared/types"
import { getOrgPropertyStringValue, useIsSysAdmin } from "@/shared/utils"

import {
  Box,
  Button,
  Copy,
  Flex,
  Redacted,
  Spacer,
  Stack,
  Switch,
  SwitchOption,
  Title,
} from "@/shared/components"

export const OrganizationOrderSettings = () => {
  const { organizationId } = useParams<keyof RouteParams>() as RouteParams
  const isSysAdmin = useIsSysAdmin()
  const { data: properties, isLoading } = useOrderOrgProperties(organizationId)
  const { mutate: saveProperties, isLoading: isSaving } = useUpdateOrderOrgProperties(organizationId)

  const [reprocessEnabled, setReprocessEnabled] = useState(false)

  useEffect(() => {
    if (!properties) {
      return
    }

    setReprocessEnabled(
      getOrgPropertyStringValue(properties, OrgProperties.FEATURE_FLAG_UI_ORDER_REPROCESS_ENABLED) ===
        "true",
    )
  }, [properties])

  const handleSave = () => {
    saveProperties([
      {
        propertyKey: OrgProperties.FEATURE_FLAG_UI_ORDER_REPROCESS_ENABLED,
        value: String(reprocessEnabled),
      },
    ])
  }

  if (!isSysAdmin) {
    return null
  }

  if (isLoading) {
    return (
      <Box css={{ marginTop: "$32" }}>
        <Redacted height="22px" width="240px" text animated />
      </Box>
    )
  }

  return (
    <Box css={{ marginTop: "$32" }}>
      <Title as="h2" scale={6} color="theme-b-n3">
        Order settings
      </Title>
      <Spacer size={24} />
      <Stack space={24}>
        <Flex align="center" css={{ gap: "$12" }}>
          <Switch
            name="uiOrderReprocessEnabled"
            value={reprocessEnabled ? "true" : "false"}
            onValueChange={(value) => setReprocessEnabled(value === "true")}
            checked={reprocessEnabled}
            disabled={false}
          >
            <SwitchOption value={reprocessEnabled ? "false" : "true"} />
          </Switch>
          <Copy scale={10}>Enable UI order reprocess</Copy>
        </Flex>

        <Flex>
          <Button
              action="primary"
              size="small"
              type="button"
              ariaLabel="Save order settings"
              loading={isSaving}
              onClick={handleSave}
            >
              <Copy as="span" fontWeight="bold">
                Save settings
              </Copy>
            </Button>
          </Flex>
      </Stack>
    </Box>
  )
}
